// src/App.js

import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation
} from 'react-router-dom';
import Sidebar from './components/Sidebar';
import History from './pages/History';
import NewAudio from './pages/NewAudio';
import Settings from './pages/Settings';
import Login from './pages/Login';
import AuthAction from './pages/AuthAction';
import Pricing from './pages/Pricing';
import RecordingDetailsRoute from './pages/RecordingDetailsRoute';
import TextToSpeech from './pages/TextToSpeech';
import Reports from './pages/Reports';
import API from './pages/API';
import Checklists from './pages/ChecklistManager'; // New import
import AIAgents from './pages/AIAgents';
import AgentConversationDetails from './pages/AgentConversationDetails';
import OnboardingModal from './components/OnboardingModal'; // New import
import ImpersonationBanner from './components/ImpersonationBanner';
import MaintenanceBanner from './components/MaintenanceBanner';
import Admin from './pages/Admin';
import { Box, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { auth, db } from './firebase'; // Import db
import { doc, updateDoc } from 'firebase/firestore'; // New imports
import Clarity from '@microsoft/clarity';
import * as amplitude from '@amplitude/analytics-browser';
import { UserDataProvider, useUserData } from './hooks/useUserData';
import { canAccessAIAgents } from './planAccess';

const AppContent = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false); // New state for modal
  const location = useLocation();

  const { userData, loadingUserData } = useUserData();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      if (u) {
        await u.getIdToken(true);
        setUser(u);
        amplitude.track('User Signed In', { userId: u.uid, email: u.email });
      } else {
        setUser(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Effect to control the onboarding modal
  useEffect(() => {
    if (user && userData && !loadingUserData && !userData.onboardingCompleted) {
      setOnboardingOpen(true);
    } else {
      setOnboardingOpen(false);
    }
  }, [user, userData, loadingUserData]);

  useEffect(() => {
    Clarity.init('oybify9h4t');
  }, []);

  useEffect(() => {
    amplitude.init('c57879646836f2336d864a17f4a56b7c', { autocapture: true });
  }, []);

  useEffect(() => {
    amplitude.track('Page Viewed', { path: location.pathname });
    const titles = {
      '/history': 'Պատմություն - xosum.am',
      '/new': 'Նոր վերծանում - xosum.am',
      '/settings': 'Կարգավորումներ - xosum.am',
      '/pricing': 'Գնառաջարկ - xosum.am',
      '/login': 'Xosum.am - Հայերեն խոսքը տեքստի վերածող գործիք',
      '/text-to-speech': 'Տեքստից խոսք - xosum.am',
      '/reports': 'Վիճակագրություն - xosum.am',
      '/api': 'API և Ինտեգրացիա - xosum.am',
      '/ai-analysis': 'AI Վերլուծություն - xosum.am',
      '/checklists': 'AI Վերլուծություն - xosum.am',
      '/ai-agents': 'AI Ագենտներ - xosum.am',
    };
    if (location.pathname.startsWith('/recording-details')) {
      document.title = 'Մանրամասներ - xosum.am';
    } else if (location.pathname.startsWith('/ai-analysis')) {
      document.title = 'AI Վերլուծություն - xosum.am';
    } else if (location.pathname.startsWith('/ai-agents')) {
      document.title = 'AI Ագենտներ - xosum.am';
    } else {
      document.title = titles[location.pathname] || 'Xosum.am - Հայերեն խոսքը տեքստի վերածող գործիք';
    }
  }, [location]);

  const handleDrawerToggle = () => setMobileOpen((prevOpen) => !prevOpen);
  const handleDrawerClose = () => setMobileOpen(false);
  const handleLogout = async () => {
    try {
      await auth.signOut();
      amplitude.track('User Logged Out');
    } catch (e) {
      console.error('Error during sign out:', e);
    }
  };

  const handleSaveOnboarding = async (data) => {
    if (user && user.uid) {
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, data);
        if (data.onboarding) {
          amplitude.track('ui_onboarding_success', data.onboarding);
        }
        setOnboardingOpen(false);
      } catch (error) {
        console.error("Error saving onboarding data:", error);
        // Optionally, you can show a user-facing error message here
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, minHeight: '100vh' }}>
      <MaintenanceBanner />
      {user && <ImpersonationBanner />}
      <Box sx={{ display: 'flex', width: '100%', minWidth: 0, flexGrow: 1 }}>
      {user && <OnboardingModal open={onboardingOpen} onSave={handleSaveOnboarding} user={userData} />}

      {user && (
        <IconButton
          color="primary"
          aria-label="open drawer"
          edge="start"
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1300, // zIndex.drawer + 1
            display: { sm: 'none' },
            backgroundColor: 'white',
            boxShadow: 3,
            '&:hover': {
              backgroundColor: 'grey.200',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      {user && (
        <Sidebar
          user={user}
          mobileOpen={mobileOpen}
          handleDrawerClose={handleDrawerClose}
        />
      )}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          minWidth: 0,
          p: { xs: 1.25, sm: 3 },
          backgroundColor: '#f5f5f5',
          minHeight: '100vh',
        }}
      >
        <Routes>
          <Route path="/history" element={<ProtectedRoute user={user} loading={loading}><History /></ProtectedRoute>} />
          <Route path="/new" element={<ProtectedRoute user={user} loading={loading}><NewAudio /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute user={user} loading={loading}><Settings /></ProtectedRoute>} />
          <Route path="/recording-details/:docId" element={<ProtectedRoute user={user} loading={loading}><RecordingDetailsRoute /></ProtectedRoute>} />
          <Route path="/pricing" element={<ProtectedRoute user={user} loading={loading}><PricingRoute userData={userData} loadingUserData={loadingUserData} /></ProtectedRoute>} />
          <Route path="/text-to-speech" element={<ProtectedRoute user={user} loading={loading}><TextToSpeech /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute user={user} loading={loading}><Reports /></ProtectedRoute>} />
          <Route path="/api" element={<ProtectedRoute user={user} loading={loading}><API /></ProtectedRoute>} />
          <Route path="/ai-analysis" element={<ProtectedRoute user={user} loading={loading}><Checklists /></ProtectedRoute>} />
          <Route path="/ai-analysis/:checklistId" element={<ProtectedRoute user={user} loading={loading}><Checklists /></ProtectedRoute>} />
          <Route path="/checklists" element={<ProtectedRoute user={user} loading={loading}><Navigate to="/ai-analysis" replace /></ProtectedRoute>} />
          <Route path="/ai-agents" element={<ProtectedRoute user={user} loading={loading}><AIAgents user={user} /></ProtectedRoute>} />
          <Route path="/ai-agents/new" element={<ProtectedRoute user={user} loading={loading}><AIAgents user={user} /></ProtectedRoute>} />
          <Route path="/ai-agents/:agentId/edit" element={<ProtectedRoute user={user} loading={loading}><AIAgents user={user} /></ProtectedRoute>} />
          <Route path="/ai-agents/:agentId" element={<ProtectedRoute user={user} loading={loading}><AIAgents user={user} /></ProtectedRoute>} />
          <Route path="/ai-agents/:agentId/conversations/:conversationId" element={<ProtectedRoute user={user} loading={loading}><AgentConversationRoute userData={userData} loadingUserData={loadingUserData} /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute user={user} loading={loading}><Admin /></ProtectedRoute>} />
          <Route path="/login" element={<LoginRoute user={user} />} />
          <Route path="/auth/action" element={<AuthAction />} />
          <Route path="*" element={<Navigate to="/new" />} />
        </Routes>
      </Box>
      </Box>
    </Box>
  );
};

// If the user is already signed in, honor a ?returnTo query (used by the
// "Exit impersonation" flow to send the re-authenticated admin straight
// back to /admin). Otherwise fall back to /new.
const LoginRoute = ({ user }) => {
  const location = useLocation();
  if (!user) return <Login />;
  const params = new URLSearchParams(location.search);
  const returnTo = params.get('returnTo');
  const safeReturnTo =
    returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')
      ? returnTo
      : '/new';
  return <Navigate to={safeReturnTo} replace />;
};

const ProtectedRoute = ({ user, loading, children }) => {
  if (loading) return <div>Բեռնվում է...</div>;
  if (!user) return <Navigate to="/login" />;
  return children;
};

const PricingRoute = ({ userData, loadingUserData }) => {
  if (loadingUserData) return <div>Բեռնվում է...</div>;
  // If user has a paid plan, redirect to settings
  if (userData?.plan && userData.plan !== 'free') {
    return <Navigate to="/settings" />;
  }
  return <Pricing />;
};

const AgentConversationRoute = ({ userData, loadingUserData }) => {
  if (loadingUserData) return <div>Բեռնվում է...</div>;
  if (!canAccessAIAgents(userData?.plan)) {
    return <Navigate to="/ai-agents" replace />;
  }
  return <AgentConversationDetails />;
};

const App = () => (
  <UserDataProvider>
    <Router>
      <AppContent />
    </Router>
  </UserDataProvider>
);

export default App;
